import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Fade } from 'react-awesome-reveal';
import { Icon } from '../Icon';
import styles from './InputV2.css';

const cx = classnames.bind(styles);

function InputV2({
    value,
    placeholder,
    errorText,
    defaultValue,
    name,
    type,
    inputProps,
    className,
    label,
    onChange,
    onBlur,
    disabled,
    onKeyDown,
    inputRef,
    iconName,
    emailDomainSuggestions,
    didYouMean,
    didYouMeanPrefix = 'Did you mean',
    didYouMeanSuffix = 'for your email?'
}) {
    const handleChange = useCallback(e => {
        if (onChange) {
            onChange(name, e.target.value);
        }
    }, [onChange, name]);

    const handleBlur = e => {
        if (onBlur) {
            onBlur(e.target.value);
        }
    };

    const renderInput = () => {
        const inputClasses = cx({
            input: true,
            error: Boolean(errorText)
        });

        return (
            <input
                id               = {name}
                className        = {inputClasses}
                defaultValue     = {defaultValue}
                value            = {value}
                name             = {name}
                type             = {type || 'text'}
                placeholder      = {placeholder}
                onChange         = {handleChange}
                onBlur           = {handleBlur}
                onKeyDown        = {onKeyDown}
                autoComplete     = 'off'
                disabled         = {disabled}
                ref              = {inputRef}
                aria-invalid     = {Boolean(errorText) || undefined}
                aria-describedby = {errorText ? `${name}-error` : undefined}
                {...inputProps}
            />
        );
    };

    const renderEmailDomainSuggestions = () => {
        if (!emailDomainSuggestions?.list) {
            return null;
        }

        if (value.trim().length >= 3 && !value.includes('@')) {
            return (
                <Fade triggerOnce duration={300}>
                    <ul className={styles.emailDomainSuggestions}>
                        {emailDomainSuggestions.list.map(emailDomain => (
                            <li
                                key={emailDomain}
                                onClick={() => onChange(name, `${value}@${emailDomain}`)}
                            >
                                {value}@{emailDomain}
                            </li>
                        ))}
                    </ul>
                </Fade>
            );
        }

        return null;
    };

    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });

    return (
        <div className={classes}>
            {Boolean(label) && (
                <label className={styles.label} htmlFor={name}>
                    {label}
                </label>
            )}
            <div className={styles.container}>
                {renderInput()}
                {Boolean(errorText) && (
                    <Icon className={cx('icon', 'error')} name='alertTriangle' />
                )}
                {!errorText && Boolean(iconName) && (
                    <Icon className={styles.icon} name={iconName} />
                )}
                {renderEmailDomainSuggestions()}
            </div>
            {Boolean(errorText) && (
                <Fade triggerOnce duration={300}>
                    <p id={`${name}-error`} className={styles.alert} role='alert'>{errorText}</p>
                </Fade>
            )}
            {Boolean(didYouMean) && (
                <Fade triggerOnce duration={300}>
                    <p className={styles.didYouMeanText}>
                        {didYouMeanPrefix}{' '}
                        <button
                            type='button'
                            className={styles.didYouMeanSuggestion}
                            onClick={() => onChange(name, didYouMean)}
                        >
                            {didYouMean}
                        </button>{' '}
                        {didYouMeanSuffix}
                    </p>
                </Fade>
            )}
        </div>
    );
}

InputV2.propTypes = {
    type: PropTypes.string,
    value: PropTypes.string,
    iconName: PropTypes.string,
    name: PropTypes.string,
    defaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    errorText: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    className: PropTypes.string,
    inputProps: PropTypes.object,
    inputRef: PropTypes.object,
    emailDomainSuggestions: PropTypes.object,
    didYouMean: PropTypes.string,
    didYouMeanPrefix: PropTypes.node,
    didYouMeanSuffix: PropTypes.node
};

export {
    InputV2
};
